import React, { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Pagination } from '@material-ui/lab';
import { useDispatch } from 'react-redux';
import { lazyFetch } from '@configuratorware/configurator-frontendgui/App/Utils/Request/lazyFetch';
import { T } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import GlobalLoader from '@configuratorware/configurator-frontendgui/App/Shared/Containers/GlobalLoader';
import { withGlobalLoader } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import ItemTile from './ItemTile';
import ThemeProvider from '../ThemeProvider';
import { green } from '../Colors';
import { selectOptionclassification } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';

const useStyles = makeStyles(
  (theme) => ({
    rootContainer: {
      paddingLeft: 24,
      paddingRight: 24,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 16,
        paddingRight: 16,
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      margin: -4,
    },
    itemContainer: {
      display: 'flex',
      width: '100%',
      maxWidth: 1400,
      margin: 'auto',
      borderRadius: '10px',
    },
    gridContainer: {
      margin: 'auto',
    },
    categoryContainer: {
      display: 'flex',
      gap: '15px',
      width: '100%',
      marginTop: '40px',
      marginBottom: '30px',
      overflowX: 'auto',
      [theme.breakpoints.down('md')]: {
        marginTop: '20px',
      },
    },
    categoryButton: {
      backgroundColor: '#ededed',
      color: '#000',
      borderRadius: '50px',
      padding: '20px',
      '&.active': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
      },
    },
    header: {
      minHeight: '64px',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: '10px',
    },
    title: {
      fontSize: 'clamp(1rem, 4vw, 2rem)',
    },
    titlePart1: {
      color: green,
    },
    subtitle: {
      marginTop: 16,
      marginBottom: 32,
      fontSize: 'clamp(0.8rem, 4vw, 1rem)',
      [theme.breakpoints.down('sm')]: {
        marginTop: 0,
        marginBottom: 16,
      },
    },
  }),
  { name: 'ItemOverview' }
);

export const ItemOverview = () => {
  const pageSize = 2;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    from: 0,
  });
  const dispatch = useDispatch();

  const fetchItems = async () => {
    const params = {
      offset: pagination.from,
      limit: pageSize,
    };
    const response = await dispatch(
      withGlobalLoader(() =>
        lazyFetch({
          url: `items`,
          params,
        })
      )
    );
    return response.data;
  };

  useEffect(() => {
    dispatch(selectOptionclassification({}));
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      const { data: items, count } = await fetchItems();
      setItems(items);
      setPagination({ ...pagination, count: Math.ceil(count / pageSize) });
    })();
  }, [pagination.from]);

  const handlePageChange = (event, page) => {
    const from = (page - 1) * pageSize;
    setPagination({ ...pagination, from: from });
  };

  const renderPagination = () => {
    return (
      pagination.count > 1 && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={pagination.count}
            color="primary"
            onChange={handlePageChange}
            size={isMobile ? 'small' : 'large'}
          />
        </div>
      )
    );
  };

  const renderItems = () => {
    if (items.length < 1) {
      return (
        <div>
          <Typography variant="body2">{T('itemOverview.noItems')}</Typography>
        </div>
      );
    } else {
      return (
        <div className={classes.itemContainer}>
          {renderPagination()}
          <Grid container className={classes.gridContainer} spacing={1}>
            {items.map((item, index) => (
              <Grid item key={item.identifier} xs={12} sm={6}>
                <ItemTile
                  title={item.title}
                  index={index}
                  image={item.detailImage}
                  linkTo={item.identifier}
                  key={item.identifier}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      );
    }
  };

  return (
    <ThemeProvider>
      <div className={classes.rootContainer}>
        <AppBar color="default" className={classes.header} position="static">
          <Typography variant="h4" className={classes.title}>
            <span className={classes.titlePart1}>{T('itemOverview.title1')}</span>
            {T('itemOverview.title2')}
          </Typography>
        </AppBar>
        <Typography variant="h5" className={classes.subtitle}>
          {T('itemOverview.selectWallType')}
        </Typography>
        <div className={classes.container}>{!!items.length && renderItems()}</div>
        <GlobalLoader />
      </div>
    </ThemeProvider>
  );
};

export default ItemOverview;
